import { useCallback, useEffect, useMemo, useState } from "react";
import { ComboBoxOption } from "@shared/designSystem/ComboBox";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import {
    CollaboratorCustomField,
    CollaboratorCustomFieldListValue,
    CustomFieldsService,
} from "@services/CollaboratorCustomFieldsService";

// Shared by every custom fields settings screen (collaborators, qontact points...).
// Each screen only provides its service and its texts.

interface CreateFieldDraft {
    name: string;
    type: CustomFieldTypeOption | undefined;
    isRequired: boolean;
    listValue: string;
    listValues: CollaboratorCustomFieldListValue[];
}

export interface CustomFieldTypeOption extends ComboBoxOption {
    description: string;
    example: string;
}

/** Example shown on each field type card, per kind of custom fields. Already localized. */
export interface CustomFieldTypeExamples {
    text: string;
    number: string;
    date: string;
    boolean: string;
    list: string;
}

const buildFieldTypeOptions = (examples: CustomFieldTypeExamples): CustomFieldTypeOption[] => [
    {
        id: "text",
        title: "Texto".localized(),
        description: "Respuesta libre para datos cortos.".localized(),
        example: `${"Ejemplo".localized()}: ${examples.text}`,
    },
    {
        id: "number",
        title: "Número".localized(),
        description: "Sólo acepta valores numéricos.".localized(),
        example: `${"Ejemplo".localized()}: ${examples.number}`,
    },
    {
        id: "date",
        title: "Fecha".localized(),
        description: "Selector de fecha para datos temporales.".localized(),
        example: `${"Ejemplo".localized()}: ${examples.date}`,
    },
    {
        id: "boolean",
        title: "Sí/No".localized(),
        description: "Respuesta simple de confirmación.".localized(),
        example: `${"Ejemplo".localized()}: ${examples.boolean}`,
    },
    {
        id: "list",
        title: "Lista".localized(),
        description: "Selector con valores predefinidos.".localized(),
        example: `${"Ejemplo".localized()}: ${examples.list}`,
    },
];

export interface CustomFieldsConfigOptions {
    /** Example shown on each field type card in the create modal */
    typeExamples: CustomFieldTypeExamples;
    /** Toast shown when the fields can't be loaded */
    loadErrorText: string;
    /** Toast shown when editing a list removes an option that is in use */
    editValueInUseErrorText: string;
    /** Toast shown when deleting a field that has data (if no dialog is used) */
    deleteInUseErrorText: string;
    /**
     * When deleting a field that already has data, open a dialog suggesting to
     * deactivate it instead of only showing an error toast.
     */
    suggestDeactivateOnDeleteInUse?: boolean;
}

interface CustomFieldsConfigScreenViewModelProps {
    orgID: string;
    customFieldsService: CustomFieldsService;
    options: CustomFieldsConfigOptions;
}

export const useCustomFieldsConfigScreenViewModel = ({
    orgID,
    customFieldsService,
    options,
}: CustomFieldsConfigScreenViewModelProps) => {
    const [rows, setRows] = useState<CollaboratorCustomField[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingFieldID, setEditingFieldID] = useState<string | null>(null);
    const [editingName, setEditingName] = useState("");
    const [editingListValue, setEditingListValue] = useState("");
    const [editingListValues, setEditingListValues] = useState<CollaboratorCustomFieldListValue[]>([]);
    const [fieldInUse, setFieldInUse] = useState<CollaboratorCustomField | null>(null);
    const [draft, setDraft] = useState<CreateFieldDraft>({
        name: "",
        type: undefined,
        isRequired: false,
        listValue: "",
        listValues: [],
    });
    const [errors, setErrors] = useState<{ name: string; type: string; listValue: string; editName: string; editListValue: string }>({
        name: "",
        type: "",
        listValue: "",
        editName: "",
        editListValue: "",
    });

    const loadRows = useCallback(async () => {
        setIsLoading(true);

        try {
            const fields = await customFieldsService.getFields(orgID);
            setRows(fields.filter((field) => field.typeId !== "options"));
        } catch (error) {
            ToastManager.shared().showErrorMessage(options.loadErrorText);
        } finally {
            setIsLoading(false);
        }
    }, [customFieldsService, orgID, options.loadErrorText]);

    useEffect(() => {
        void loadRows();
    }, [loadRows]);

    const fieldTypeOptions = useMemo(
        () => buildFieldTypeOptions(options.typeExamples),
        [options.typeExamples],
    );

    const sortedRows = useMemo(() => {
        return [...rows].sort((left, right) => left.name.localeCompare(right.name));
    }, [rows]);

    const openCreateModal = () => setIsCreateModalOpen(true);

    const closeCreateModal = () => {
        setIsCreateModalOpen(false);
        setDraft({
            name: "",
            type: undefined,
            isRequired: false,
            listValue: "",
            listValues: [],
        });
        setErrors({ name: "", type: "", listValue: "", editName: "", editListValue: "" });
    };

    const updateDraftName = (value: string) => {
        setDraft((current) => ({ ...current, name: value }));
        setErrors((current) => ({ ...current, name: "" }));
    };

    const updateDraftType = (option: CustomFieldTypeOption) => {
        setDraft((current) => ({
            ...current,
            type: option,
            isRequired: option.id === "boolean" ? false : current.isRequired,
        }));
        setErrors((current) => ({ ...current, type: "" }));
    };

    const updateDraftListValue = (value: string) => {
        setDraft((current) => ({ ...current, listValue: value }));
        setErrors((current) => ({ ...current, listValue: "" }));
    };

    const addDraftListValue = () => {
        const result = buildListValue(draft.listValue, draft.listValues);

        if (result.error) {
            setErrors((current) => ({ ...current, listValue: result.error || "" }));
            return;
        }

        if (!result.value) {
            return;
        }

        setDraft((current) => ({
            ...current,
            listValue: "",
            listValues: [...current.listValues, result.value as CollaboratorCustomFieldListValue],
        }));
    };

    const removeDraftListValue = (valueID: string) => {
        setDraft((current) => ({
            ...current,
            listValues: current.listValues.filter((value) => value.id !== valueID),
        }));
        setErrors((current) => ({ ...current, listValue: "" }));
    };

    const toggleDraftRequired = () => {
        if (draft.type?.id === "boolean") {
            return;
        }

        setDraft((current) => ({ ...current, isRequired: !current.isRequired }));
    };

    const toggleField = async (fieldID: string) => {
        const currentRow = rows.find((row) => row.id === fieldID);

        if (!currentRow || isSaving) {
            return;
        }

        setIsSaving(true);

        try {
            const updatedRow = await customFieldsService.updateField(orgID, fieldID, {
                isActive: !currentRow.isActive,
            });
            setRows((current) => current.map((row) => row.id === fieldID ? updatedRow : row));
            ToastManager.shared().showSuccessMessage(
                updatedRow.isActive
                    ? `${updatedRow.name} ${"reactivado exitosamente".localized()}`
                    : `${updatedRow.name} ${"desactivado exitosamente".localized()}`
            );
        } catch (error) {
            ToastManager.shared().showErrorMessage("No se pudo actualizar el campo".localized());
        } finally {
            setIsSaving(false);
        }
    };

    const toggleRequiredField = async (fieldID: string) => {
        const currentRow = rows.find((row) => row.id === fieldID);

        if (!currentRow || currentRow.typeId === "boolean" || isSaving) {
            return;
        }

        setIsSaving(true);

        try {
            const updatedRow = await customFieldsService.updateField(orgID, fieldID, {
                isRequired: !currentRow.isRequired,
            });
            setRows((current) => current.map((row) => row.id === fieldID ? updatedRow : row));
            ToastManager.shared().showSuccessMessage(
                updatedRow.isRequired
                    ? `${updatedRow.name} ${"ahora es obligatorio".localized()}`
                    : `${updatedRow.name} ${"ya no es obligatorio".localized()}`
            );
        } catch (error) {
            ToastManager.shared().showErrorMessage("No se pudo actualizar el campo".localized());
        } finally {
            setIsSaving(false);
        }
    };

    const startEditingField = (field: CollaboratorCustomField) => {
        setEditingFieldID(field.id);
        setEditingName(field.name);
        setEditingListValue("");
        setEditingListValues(field.values || []);
        setErrors((current) => ({ ...current, editName: "", editListValue: "" }));
    };

    const cancelEditingField = () => {
        setEditingFieldID(null);
        setEditingName("");
        setEditingListValue("");
        setEditingListValues([]);
        setErrors((current) => ({ ...current, editName: "", editListValue: "" }));
    };

    const updateEditingName = (value: string) => {
        setEditingName(value);
        setErrors((current) => ({ ...current, editName: "" }));
    };

    const updateEditingListValue = (value: string) => {
        setEditingListValue(value);
        setErrors((current) => ({ ...current, editListValue: "" }));
    };

    const addEditingListValue = () => {
        const result = buildListValue(editingListValue, editingListValues);

        if (result.error) {
            setErrors((current) => ({ ...current, editListValue: result.error || "" }));
            return;
        }

        if (!result.value) {
            return;
        }

        setEditingListValues((current) => [...current, result.value as CollaboratorCustomFieldListValue]);
        setEditingListValue("");
    };

    const removeEditingListValue = (valueID: string) => {
        setEditingListValues((current) => current.filter((value) => value.id !== valueID));
        setErrors((current) => ({ ...current, editListValue: "" }));
    };

    const submitEditField = async () => {
        const fieldID = editingFieldID;
        const trimmedName = editingName.trim();
        const formattedName = formatDisplayName(trimmedName);
        const currentField = rows.find((row) => row.id === fieldID);

        if (!fieldID) {
            return;
        }

        if (!trimmedName) {
            setErrors((current) => ({ ...current, editName: "El nombre es obligatorio".localized() }));
            return;
        }

        const duplicated = rows.some(
            (row) => row.id !== fieldID && row.name.toLowerCase() === formattedName.toLowerCase()
        );

        if (duplicated) {
            setErrors((current) => ({ ...current, editName: "Ya existe un campo con ese nombre".localized() }));
            return;
        }

        if (currentField?.typeId === "list" && editingListValues.length === 0) {
            setErrors((current) => ({ ...current, editListValue: "La lista debe tener al menos un valor".localized() }));
            return;
        }

        const hasNameChanged = currentField?.name !== formattedName;
        const hasListValuesChanged = currentField?.typeId === "list"
            ? !areListValuesEqual(currentField.values || [], editingListValues)
            : false;

        if (!hasNameChanged && !hasListValuesChanged) {
            ToastManager.shared().showInfoMessage("No se realizaron cambios".localized());
            return;
        }

        setIsSaving(true);

        try {
            const updatedRow = await customFieldsService.updateField(orgID, fieldID, {
                name: formattedName,
                values: currentField?.typeId === "list" ? editingListValues : undefined,
            });
            setRows((current) => current.map((row) => row.id === fieldID ? updatedRow : row));
            cancelEditingField();
            ToastManager.shared().showSuccessMessage("Campo actualizado".localized());
        } catch (error) {
            ToastManager.shared().showErrorMessage(options.editValueInUseErrorText);
        } finally {
            setIsSaving(false);
        }
    };

    const deleteField = async (fieldID: string) => {
        if (isSaving) {
            return;
        }

        setIsSaving(true);

        try {
            await customFieldsService.deleteField(orgID, fieldID);
            setRows((current) => current.filter((row) => row.id !== fieldID));
            ToastManager.shared().showSuccessMessage("Campo eliminado".localized());
        } catch (error: any) {
            // 409 = FIELD_IN_USE: some record already has data in this field
            if (options.suggestDeactivateOnDeleteInUse && Number(error?.statusCode) === 409) {
                setFieldInUse(rows.find((row) => row.id === fieldID) ?? null);
            } else {
                ToastManager.shared().showErrorMessage(options.deleteInUseErrorText);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const closeFieldInUseDialog = () => setFieldInUse(null);

    const deactivateFieldInUse = async () => {
        if (!fieldInUse) {
            return;
        }
        const fieldID = fieldInUse.id;
        setFieldInUse(null);
        if (rows.find((row) => row.id === fieldID)?.isActive) {
            await toggleField(fieldID);
        }
    };

    const submitCreate = async () => {
        const trimmedName = draft.name.trim();
        const formattedName = formatDisplayName(trimmedName);
        const nextErrors = { name: "", type: "", listValue: "", editName: "", editListValue: "" };

        if (!trimmedName) {
            nextErrors.name = "El nombre es obligatorio".localized();
        }

        if (!draft.type) {
            nextErrors.type = "Debes seleccionar un tipo".localized();
        }

        if (draft.type?.id === "list" && draft.listValues.length === 0) {
            nextErrors.listValue = "Agregá al menos un valor para la lista".localized();
        }

        const duplicated = rows.some(
            (row) => row.name.toLowerCase() === formattedName.toLowerCase()
        );

        if (duplicated) {
            nextErrors.name = "Ya existe un campo con ese nombre".localized();
        }

        setErrors(nextErrors);

        if (nextErrors.name || nextErrors.type || nextErrors.listValue || !draft.type) {
            return;
        }

        setIsSaving(true);

        try {
            const createdField = await customFieldsService.createField(orgID, {
                id: slugify(formattedName),
                name: formattedName,
                typeId: draft.type.id,
                typeLabel: draft.type.title,
                isRequired: draft.type.id === "boolean" ? false : draft.isRequired,
                isActive: true,
                values: draft.type.id === "list" ? draft.listValues : undefined,
            });

            setRows((current) => [...current, createdField]);
            ToastManager.shared().showSuccessMessage("Campo creado".localized());
            closeCreateModal();
        } catch (error) {
            ToastManager.shared().showErrorMessage("No se pudo crear el campo".localized());
        } finally {
            setIsSaving(false);
        }
    };

    return {
        rows: sortedRows,
        fieldTypeOptions,
        isLoading,
        isSaving,
        isCreateModalOpen,
        editingFieldID,
        editingName,
        draft,
        editingListValue,
        editingListValues,
        errors,
        fieldInUse,
        isCreateDisabled:
            isSaving ||
            !draft.name.trim() ||
            !draft.type ||
            (draft.type.id === "list" && draft.listValues.length === 0),
        openCreateModal,
        closeCreateModal,
        updateDraftName,
        updateDraftType,
        updateDraftListValue,
        addDraftListValue,
        removeDraftListValue,
        toggleDraftRequired,
        toggleField,
        toggleRequiredField,
        startEditingField,
        cancelEditingField,
        updateEditingName,
        updateEditingListValue,
        addEditingListValue,
        removeEditingListValue,
        submitEditField,
        deleteField,
        closeFieldInUseDialog,
        deactivateFieldInUse,
        submitCreate,
    };
};

const slugify = (value: string) => {
    return value
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
};

const normalizeListValue = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");

const formatDisplayName = (value: string) => {
    const normalized = value.trim().replace(/\s+/g, " ").toLowerCase();
    return normalized ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}` : "";
};

const buildListValue = (
    rawValue: string,
    currentValues: CollaboratorCustomFieldListValue[],
): { value?: CollaboratorCustomFieldListValue; error?: string } => {
    const label = formatDisplayName(rawValue);

    if (!label) {
        return { error: "Ingresá un valor".localized() };
    }

    const duplicated = currentValues.some(
        (value) => normalizeListValue(value.label) === normalizeListValue(label)
    );

    if (duplicated) {
        return { error: "Ese valor ya existe en la lista".localized() };
    }

    return {
        value: {
            id: slugify(label),
            label,
        },
    };
};

const areListValuesEqual = (
    leftValues: CollaboratorCustomFieldListValue[],
    rightValues: CollaboratorCustomFieldListValue[],
) => {
    if (leftValues.length !== rightValues.length) {
        return false;
    }

    return leftValues.every((leftValue, index) => {
        const rightValue = rightValues[index];
        return leftValue.id === rightValue?.id && leftValue.label === rightValue?.label;
    });
};
